import React, { useState } from "react";
import {
  Button,
  Card,
  Col,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useVentas } from "../../../hooks/useVentas";
import { Pedido, PagoEstado, MetodoPago } from "../../../services/ventas/ventas.types";

const { Title } = Typography;

const formatMXN = (pesos: number) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
  }).format(pesos);

const ESTADO_LABEL: Record<PagoEstado, string> = {
  PAGADO: "Pagado",
  PENDIENTE_PAGO: "Pendiente",
  EXPIRADO: "Expirado",
  FALLIDO: "Fallido",
};

const ESTADO_COLOR: Record<PagoEstado, string> = {
  PAGADO: "green",
  PENDIENTE_PAGO: "gold",
  EXPIRADO: "default",
  FALLIDO: "red",
};

const METODO_LABEL: Record<MetodoPago, string> = {
  CARD: "Tarjeta",
  CASH: "Efectivo (OXXO)",
  BANK_TRANSFER: "Transferencia (SPEI)",
  PAY_BY_BANK: "BBVA",
};

const METODO_OPCIONES: { value: MetodoPago; label: string }[] = [
  { value: "CARD", label: "Tarjeta" },
  { value: "CASH", label: "Efectivo (OXXO)" },
  { value: "BANK_TRANSFER", label: "Transferencia (SPEI)" },
  { value: "PAY_BY_BANK", label: "BBVA" },
];

const ESTADO_OPCIONES: { value: PagoEstado; label: string }[] = [
  { value: "PAGADO", label: "Pagado" },
  { value: "PENDIENTE_PAGO", label: "Pendiente" },
  { value: "EXPIRADO", label: "Expirado" },
  { value: "FALLIDO", label: "Fallido" },
];

const formatFecha = (iso: string) => {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
};

const VentasPage: React.FC = () => {
  const { ventas, loading } = useVentas();

  const [filtroEstado, setFiltroEstado] = useState<PagoEstado | undefined>();
  const [filtroMetodo, setFiltroMetodo] = useState<MetodoPago | undefined>();

  if (loading) return <Spin size="large" />;

  const pagados = ventas.filter((p) => p.pagoEstado === "PAGADO");
  const totalVendido = pagados.reduce((sum, p) => sum + p.total, 0);

  const totalPorMetodo = (metodo: MetodoPago) =>
    pagados.filter((p) => p.metodoPago === metodo).reduce((s, p) => s + p.total, 0);

  const countPorMetodo = (metodo: MetodoPago) =>
    pagados.filter((p) => p.metodoPago === metodo).length;

  const countPorEstado = (estado: PagoEstado) =>
    ventas.filter((p) => p.pagoEstado === estado).length;

  const dataFiltrada = ventas.filter((p) => {
    if (filtroEstado && p.pagoEstado !== filtroEstado) return false;
    if (filtroMetodo && p.metodoPago !== filtroMetodo) return false;
    return true;
  });

  const exportarExcel = () => {
    const sep = ";";
    const headers = [
      "Fecha",
      "Comprador",
      "Email",
      "Colegio",
      "Alumno",
      "Paquete",
      "Importe",
      "Medio",
      "Estado",
      "Order ID",
    ];
    const filas: string[][] = [];
    dataFiltrada.forEach((p) => {
      const medio = p.metodoPago ? METODO_LABEL[p.metodoPago] : "";
      const estado = ESTADO_LABEL[p.pagoEstado];
      const items = p.items && p.items.length ? p.items : [null];
      items.forEach((it) => {
        filas.push([
          formatFecha(p.createdAt),
          p.compradorNombre,
          p.compradorEmail,
          `Colegio #${p.schoolId}`,
          it ? it.nombreAlumno : "",
          it ? it.tituloSnapshot : "",
          it ? String(it.precioSnapshot) : String(p.total),
          medio,
          estado,
          p.orderId,
        ]);
      });
    });
    const escape = (v: string) =>
      /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    const csv = [headers, ...filas]
      .map((r) => r.map((c) => escape(c ?? "")).join(sep))
      .join("\n");
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ventas_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: ColumnsType<Pedido> = [
    {
      title: "Fecha",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v: string) => formatFecha(v),
      width: 140,
    },
    {
      title: "Comprador",
      key: "comprador",
      render: (_: unknown, record: Pedido) => (
        <div>
          <div>{record.compradorNombre}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: "block" }}>
            {record.compradorEmail}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: "Colegio",
      dataIndex: "schoolId",
      key: "schoolId",
      render: (v: number) => `Colegio #${v}`,
      width: 110,
    },
    {
      title: "Alumno(s) / Paquete(s)",
      key: "items",
      render: (_: unknown, record: Pedido) => (
        <div>
          {record.items?.map((it) => (
            <div key={it.id}>
              <strong>{it.nombreAlumno}</strong> — {it.tituloSnapshot}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      render: (v: number) => formatMXN(v),
      width: 110,
    },
    {
      title: "Medio",
      dataIndex: "metodoPago",
      key: "metodoPago",
      width: 170,
      render: (v: MetodoPago | null) =>
        v ? <Tag>{METODO_LABEL[v]}</Tag> : <span>—</span>,
    },
    {
      title: "Estado",
      dataIndex: "pagoEstado",
      key: "pagoEstado",
      width: 110,
      render: (v: PagoEstado) => (
        <Tag color={ESTADO_COLOR[v]}>{ESTADO_LABEL[v]}</Tag>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Ventas</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={12}>
          <Card>
            <Statistic
              title="Total vendido (pagado)"
              value={formatMXN(totalVendido)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <Card>
            <Statistic
              title="Transacciones pagadas"
              value={pagados.length}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8} md={8}>
          <Card>
            <Statistic title="Pendientes" value={countPorEstado("PENDIENTE_PAGO")} />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={8}>
          <Card>
            <Statistic title="Expirados" value={countPorEstado("EXPIRADO")} />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={8}>
          <Card>
            <Statistic title="Fallidos" value={countPorEstado("FALLIDO")} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tarjeta"
              value={`${formatMXN(totalPorMetodo("CARD"))} (${countPorMetodo("CARD")})`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Efectivo (OXXO)"
              value={`${formatMXN(totalPorMetodo("CASH"))} (${countPorMetodo("CASH")})`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Transferencia (SPEI)"
              value={`${formatMXN(totalPorMetodo("BANK_TRANSFER"))} (${countPorMetodo("BANK_TRANSFER")})`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="BBVA"
              value={`${formatMXN(totalPorMetodo("PAY_BY_BANK"))} (${countPorMetodo("PAY_BY_BANK")})`}
            />
          </Card>
        </Col>
      </Row>

      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Select
          style={{ width: 180 }}
          placeholder="Estado"
          allowClear
          value={filtroEstado}
          onChange={(v) => setFiltroEstado(v)}
          options={ESTADO_OPCIONES}
        />
        <Select
          style={{ width: 200 }}
          placeholder="Medio de pago"
          allowClear
          value={filtroMetodo}
          onChange={(v) => setFiltroMetodo(v)}
          options={METODO_OPCIONES}
        />
        <Button
          type="primary"
          onClick={exportarExcel}
          style={{ marginLeft: "auto" }}
        >
          Exportar a Excel
        </Button>
      </div>

      <Table
        dataSource={dataFiltrada}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 20 }}
      />
    </div>
  );
};

export default VentasPage;
